/**
 * Delivery risk classification (Patch E22).
 *
 * Given a project's effective delivery target and parts distribution, returns the
 * risk level driving the forecast page's sort + badge:
 *
 *   OVERDUE    effective delivery target is in the past, not yet fully delivered
 *   AT_RISK    delivery in < 7 days, more than half the parts still in
 *              procurement (won't be ready in time at current pace)
 *   WATCH      delivery in < 30 days, any parts still in procurement
 *   ON_TRACK   otherwise — parts mostly past stage 8 or plenty of time
 *
 * What "delivered" means here: the spec marks stage 12 as "Delivered", and
 * stages 13+ are post-delivery (functional check, billing chain). Our
 * coarse buckets group stages 9–13 under "inspection" — close enough for
 * the forecast: a part that has reached inspection is en route to / past
 * delivery, while a part still in procurement (stage ≤ 8) is the
 * deliverable-at-risk state.
 *
 * Special cases:
 *   - Projects with no effectiveDeliveryTargetDate are ALWAYS ON_TRACK (no risk to
 *     score against). Callers typically filter these out before showing
 *     the forecast.
 *   - Projects with no parts entered yet are scored against time alone —
 *     if they're already past target with no parts in, they're OVERDUE;
 *     within 7 days, AT_RISK; etc. Keeps the "you haven't started yet"
 *     case visible to managers.
 */

import type { ProjectProgressBuckets } from './project-progress.js';
import { totalPartsInBuckets } from './project-progress.js';

export type DeliveryRiskLevel = 'OVERDUE' | 'AT_RISK' | 'WATCH' | 'ON_TRACK';

export interface DeliveryRiskInput {
  /** ISO date string (YYYY-MM-DD), or null. */
  effectiveDeliveryTargetDate: string | null;
  buckets: ProjectProgressBuckets;
  /** Today as ISO date string in the relevant timezone (PKT for this app). */
  today: string;
}

export interface DeliveryRiskAssessment {
  level: DeliveryRiskLevel;
  /** Positive = days until target; negative = days past target; null = no target. */
  daysToTarget: number | null;
  /**
   * Fraction of parts still in the procurement bucket (stage ≤ 8). 0 when
   * the project has no parts (the time-only risk path is used instead).
   */
  procurementFraction: number;
}

/**
 * Day-count between two ISO dates (b - a) in calendar days, ignoring time
 * zones. Identical semantics to the API-side `daysBetween` helper, kept
 * here so the shared module has no runtime dependency on api/lib.
 */
function dayDiff(a: string, b: string): number {
  const A = new Date(`${a}T00:00:00Z`).getTime();
  const B = new Date(`${b}T00:00:00Z`).getTime();
  return Math.round((B - A) / 86_400_000);
}

export function assessDeliveryRisk(input: DeliveryRiskInput): DeliveryRiskAssessment {
  const { effectiveDeliveryTargetDate, buckets, today } = input;

  if (!effectiveDeliveryTargetDate) {
    return { level: 'ON_TRACK', daysToTarget: null, procurementFraction: 0 };
  }

  const daysToTarget = dayDiff(today, effectiveDeliveryTargetDate);
  const total = totalPartsInBuckets(buckets);
  const procurementFraction = total === 0 ? 0 : buckets.procurement / total;

  // Empty / no parts entered yet: scored by time alone — the "haven't
  // started" projects should still appear in the forecast.
  if (total === 0) {
    if (daysToTarget < 0) {
      return { level: 'OVERDUE', daysToTarget, procurementFraction };
    }
    if (daysToTarget < 7) {
      return { level: 'AT_RISK', daysToTarget, procurementFraction };
    }
    if (daysToTarget < 30) {
      return { level: 'WATCH', daysToTarget, procurementFraction };
    }
    return { level: 'ON_TRACK', daysToTarget, procurementFraction };
  }

  // All parts past procurement and target hasn't passed → on track regardless
  // of date (we've successfully moved everything into inspection or further).
  const allPastProcurement = buckets.procurement === 0;
  if (daysToTarget >= 0 && allPastProcurement) {
    return { level: 'ON_TRACK', daysToTarget, procurementFraction };
  }

  // Target already passed and we still have procurement work outstanding.
  if (daysToTarget < 0 && !allPastProcurement) {
    return { level: 'OVERDUE', daysToTarget, procurementFraction };
  }

  // Within 7 days with significant procurement work remaining.
  if (daysToTarget < 7 && procurementFraction > 0.5) {
    return { level: 'AT_RISK', daysToTarget, procurementFraction };
  }

  // Within 30 days with any procurement work remaining.
  if (daysToTarget < 30 && procurementFraction > 0) {
    return { level: 'WATCH', daysToTarget, procurementFraction };
  }

  return { level: 'ON_TRACK', daysToTarget, procurementFraction };
}
